import type {DataGridPremiumProps} from '@mui/x-data-grid-premium';
import {DataGridPremium, useGridApiRef} from '@mui/x-data-grid-premium';
import '~/components/muix-data-grid/BaseTableMuiX.scss';
import {
    buildDataGridClassName,
    DATA_GRID_DENSITY,
    DATA_GRID_HEADER_FILTER_HEIGHT,
    DATA_GRID_ROW_MIN_HEIGHT,
    type DataGridRowSize,
    getDataGridGroupingRowClassName,
    getDataGridHeightForCompactMin,
    getDataGridRowMinHeight,
} from './data-grid.config';

export type {DataGridRowSize} from './data-grid.config';
export {DATA_GRID_ROW_MIN_HEIGHT, DATA_GRID_HEADER_FILTER_HEIGHT};

export type CustomDataGridProps = Omit<DataGridPremiumProps, 'density' | 'rowHeight'> & {
    /** Row min height: small = 36px, medium = 72px. Density is always compact. */
    rowSize?: DataGridRowSize;
    /** When true, rows grow with content using getRowHeight={() => 'auto'}. */
    autoRowHeight?: boolean;
    /**
     * Opt in to row virtualization (off by default, as it always has been).
     * Required for features that depend on the viewport, such as the data
     * source's lazy loading.
     */
    virtualized?: boolean;
};

export const CustomDataGrid = ({
                                   rowSize = 'small',
                                   autoRowHeight = false,
                                   virtualized = false,
                                   className,
                                   getRowHeight,
                                   getRowClassName,
                                   headerFilterHeight = getDataGridHeightForCompactMin(DATA_GRID_HEADER_FILTER_HEIGHT),
                                   apiRef: apiRefProp,
                                   ...props
                               }: CustomDataGridProps) => {
    const apiRefInternal = useGridApiRef();
    const apiRef = apiRefProp ?? apiRefInternal;
    const minHeight = getDataGridRowMinHeight(rowSize);
    const mergedClassName = [buildDataGridClassName({minHeight, autoRowHeight}), className]
        .filter(Boolean)
        .join(' ');

    return (
        <DataGridPremium
            {...props}
            apiRef={apiRef}
            density={DATA_GRID_DENSITY}
            rowHeight={getDataGridHeightForCompactMin(minHeight)}
            headerFilterHeight={headerFilterHeight}
            className={mergedClassName}
            getRowHeight={autoRowHeight ? () => 'auto' : getRowHeight}
            {...!autoRowHeight && !virtualized && {disableVirtualization: true}}
            getRowClassName={(params) => {
                const groupingClassName = getDataGridGroupingRowClassName(apiRef, params);
                const userClassName = getRowClassName?.(params) ?? '';

                return [groupingClassName, userClassName].filter(Boolean).join(' ');
            }}
        />
    );
};
